import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DurationInterface, DurationPeriod, RevenueComponent, RevenuePeriod } from '@vendasta/sales-orders';
import { Observable, of } from 'rxjs';
import { DurationTotal, SummaryItem, TaxOption, UILineItem } from '../../order-form/order-item-list/interface';
import { getPeriodTranslationKey } from '../../shared/conversion-utils';
import { OrderSummaryService } from '../order-summary.service';
import { Frequency } from '@vendasta/galaxy/frequency';

@Component({
  selector: 'store-contract-estimated-total',
  templateUrl: './contract-estimated-total.component.html',
  styleUrls: ['./contract-estimated-total.component.scss'],
})
export class ContractEstimatedTotalComponent {
  @Input()
  set orderItems(orderItems: UILineItem[]) {
    const summaryItems = OrderSummaryService.getPriceSummary(orderItems);
    this.summaryItems = summaryItems.items;
    this.includesUndefinedPricing = summaryItems.includesUndefinedPricing;
  }

  @Input() taxOptions: TaxOption[];
  @Input() duration: DurationInterface;
  @Input() estimatedTotal?: DurationTotal[];

  private summaryItems: SummaryItem[];
  includesUndefinedPricing: boolean;

  constructor(private translateService: TranslateService) {}

  getDailyTotal(revenueComponent: RevenueComponent): number {
    const periodTotal = this.calculateTotalPrice(revenueComponent);
    switch (revenueComponent.period) {
      case RevenuePeriod.YEARLY:
        return periodTotal / 365;
      case RevenuePeriod.MONTHLY:
        // Average number of days in a month
        return periodTotal / 30.42;
      case RevenuePeriod.BIWEEKLY:
        return periodTotal / 14;
      case RevenuePeriod.WEEKLY:
        return periodTotal / 7;
      case RevenuePeriod.DAILY:
        return periodTotal;
      default:
        return periodTotal;
    }
  }

  getWeeklyTotal(revenueComponent: RevenueComponent): number {
    const periodTotal = this.calculateTotalPrice(revenueComponent);
    switch (revenueComponent.period) {
      case RevenuePeriod.YEARLY:
        return periodTotal / 52;
      case RevenuePeriod.MONTHLY:
        // Average number of weeks in a month
        return periodTotal / 4.333;
      case RevenuePeriod.BIWEEKLY:
        return periodTotal / 2;
      case RevenuePeriod.WEEKLY:
        return periodTotal;
      case RevenuePeriod.DAILY:
        return periodTotal * 7;
      default:
        return periodTotal;
    }
  }

  getMonthlyTotal(revenueComponent: RevenueComponent): number {
    const periodTotal = this.calculateTotalPrice(revenueComponent);
    switch (revenueComponent.period) {
      case RevenuePeriod.YEARLY:
        return periodTotal / 12;
      case RevenuePeriod.MONTHLY:
        return periodTotal;
      case RevenuePeriod.BIWEEKLY:
        return periodTotal * 2;
      case RevenuePeriod.WEEKLY:
        return periodTotal * 4;
      case RevenuePeriod.DAILY:
        return periodTotal * 30;
      default:
        return periodTotal;
    }
  }

  getYearlyTotal(revenueComponent: RevenueComponent): number {
    const periodTotal = this.calculateTotalPrice(revenueComponent);
    switch (revenueComponent.period) {
      case RevenuePeriod.YEARLY:
        return periodTotal;
      case RevenuePeriod.MONTHLY:
        return periodTotal * 12;
      case RevenuePeriod.BIWEEKLY:
        return periodTotal * 26;
      case RevenuePeriod.WEEKLY:
        return periodTotal * 52;
      case RevenuePeriod.DAILY:
        return periodTotal * 365;
      default:
        return periodTotal;
    }
  }

  getRevenuePeriod(period: RevenuePeriod): string {
    return getPeriodTranslationKey(period);
  }

  public getDurationDescription(): Observable<string> {
    if (!this.duration) {
      return of('');
    }
    switch (this.duration.duration) {
      case DurationPeriod.DAY:
        return this.translateService.stream('FRONTEND.STORE.DURATION.DAY_CONTRACT', { value: this.duration.value });
      case DurationPeriod.WEEK:
        return this.translateService.stream('FRONTEND.STORE.DURATION.WEEK_CONTRACT', { value: this.duration.value });
      case DurationPeriod.MONTH:
        return this.translateService.stream('FRONTEND.STORE.DURATION.MONTH_CONTRACT', { value: this.duration.value });
      case DurationPeriod.YEAR:
        return this.translateService.stream('FRONTEND.STORE.DURATION.YEAR_CONTRACT', { value: this.duration.value });
      default:
        return of('');
    }
  }

  public calculatePriceForDuration(): DurationTotal[] {
    if (this.estimatedTotal) {
      return this.estimatedTotal;
    }
    const results: DurationTotal[] = [];
    if (!this.duration) {
      return results;
    }
    this.summaryItems.forEach((item) => {
      let startingPrice = false;
      const total = item.revenueComponents.reduce((all, revenueComponent) => {
        if (revenueComponent.isStartingRevenue) {
          startingPrice = true;
        }

        if (revenueComponent.period === RevenuePeriod.ONETIME) {
          return all + this.calculateTotalPrice(revenueComponent);
        }

        if (this.duration.duration === DurationPeriod.DAY) {
          return all + this.getDailyTotal(revenueComponent) * this.duration.value;
        }

        if (this.duration.duration === DurationPeriod.WEEK) {
          return all + this.getWeeklyTotal(revenueComponent) * this.duration.value;
        }

        if (this.duration.duration === DurationPeriod.MONTH) {
          return all + this.getMonthlyTotal(revenueComponent) * this.duration.value;
        }

        if (this.duration.duration === DurationPeriod.YEAR) {
          return all + this.getYearlyTotal(revenueComponent) * this.duration.value;
        }

        return all + this.calculateTotalPrice(revenueComponent);
      }, 0);
      results.push({
        total: total / 100,
        currencyCode: item.currencyCode,
        startingPrice: startingPrice,
      });
    });
    return results;
  }

  public calculateTotalPrice(revenueComponent: RevenueComponent): number {
    if (revenueComponent.value === null) {
      return null;
    }
    if (!this.taxOptions) {
      return revenueComponent.value;
    }
    const taxTotal = this.taxOptions.reduce((all, tax) => all + tax.percentageMultiplier * revenueComponent.value, 0);
    return revenueComponent.value + taxTotal;
  }

  public isFree(): boolean {
    const summaryTotal = this.summaryItems.reduce((all, item) => {
      const totalPrices = item.revenueComponents.reduce((acc, revenueComponent) => acc + revenueComponent.value, 0);
      return all + totalPrices;
    }, 0);
    return summaryTotal === 0;
  }

  protected readonly Frequency = Frequency;
}
